<script setup lang="ts">
import { useGameStore } from '~/stores/games.store';
import { useSiteStore } from '~/stores/site.store';
import { HandThumbUpIcon } from '@heroicons/vue/24/solid';
import {
  useFormatCurrency,
  useFormatNumberCompact,
} from '~/composables/useFormatters';
import LazyImage from '~/components/design-system/lazy-image.vue';
import GenericTag from '../user-interface/generic-tag.vue';
import type { IGameCategory } from '~/interfaces/dto/games/response';
import type { CardProps } from '~/interfaces/components/category-card';
import GameUrlWrapper from '~/components/category/game-url-wrapper.vue';
import CategoryCardFavorite from '~/components/category/category-card-favorite.vue';
import CategoryCardNotice from '~/components/category/category-card-notice.vue';

const { $enabled } = useNuxtApp();

const site = useSiteStore();
const games = useGameStore();

const { getPlaceholder } = useBranding();

const props = withDefaults(defineProps<CardProps>(), {
  id: 0,
  image: '',
  title: '',
  game: null,
  minBetAmount: '',
  verticalTile: false,
  isCarousel: false,
  displayContent: true,
  displayHoverGlow: true,
  enableFreePlay: false,
  parentComponent: 'category-card',
  activateNotice: false,
  noticeType: 'maintenance',
  rounded: true,
});

const isProviderLobby = computed(() => {
  if (!props?.game) return false;
  if ('providerLobby' in props?.game) return props.game.providerLobby;
  return false;
});

const displayTitle = computed(() => {
  if (props.verticalTile || isProviderLobby.value) return false;
  return (
    !site.getFeatureFlags.get('website.hidegametitles') && props.displayContent
  );
});

const card = ref();
const cardHolster = ref();

const gameUnavailable = computed(() => {
  if (props.game?.unavailable) return true;
  return games.returnDisabledProviders.includes(props.game?.provider);
});

const showFavouriteButton = computed(
  () =>
    !props.enableFreePlay &&
    !site.isWebview &&
    !gameUnavailable.value &&
    !props.activateNotice,
);

const minBetFormat = computed<number>(() =>
  props.game?.minBetAmount
    ? +props.game?.minBetAmount?.replaceAll(/[a-zA-Z]/g, '')
    : 0,
);

const displayMinBet = computed(() => {
  return (
    $enabled('website.enableminbetdisplay') &&
    (!props.enableFreePlay || minBetFormat.value > 0)
  );
});
</script>
<template>
  <div
    class="relative game-card"
    :class="[
      isCarousel && 'is-carousel',
      !isCarousel && 'w-full',
      'flex flex-shrink-0',
      displayHoverGlow && 'glow',
    ]"
  >
    <CategoryCardFavorite
      v-if="showFavouriteButton"
      class="absolute top-1 right-1 p-1 z-1"
      :game="game as IGameCategory"
    />
    <GameUrlWrapper
      ref="cardHolster"
      :game="game as IGameCategory"
      :launch-as-demo-play="enableFreePlay"
      :component="props.parentComponent || 'category-card'"
      class="game-card text-base cursor-pointer bg-layer rounded-lg overflow-hidden flex-shrink-0 h-fit"
      :class="{
        'is-carousel': isCarousel,
        'w-full': !isCarousel,
        unavailable: gameUnavailable,
        'rounded-none': !rounded,
      }"
      v-bind="{
        ...gameUrlWrapperProps,
        query: {
          imgf: image.split('.')?.pop() === 'gif' ? 'anim' : 'stat',
          ...(!!index && { p: index }),
          ...gameUrlWrapperProps?.query,
        },
      }"
    >
      <div
        class="w-full justify-center relative overflow-hidden"
        :class="{
          'aspect-auto h-full': isProviderLobby,
          'aspect-[127/262] h-full': verticalTile,
          'aspect-square': !verticalTile,
        }"
      >
        <CategoryCardNotice
          v-if="gameUnavailable || activateNotice"
          :type="noticeType"
        />
        <CategoryCardLaunchButtons
          v-if="!gameUnavailable && enableFreePlay"
          class="launch-buttons hidden transition-all duration-100 ease-in-out"
        />
        <LazyImage
          :id="id"
          ref="image"
          class="w-full h-full object-cover"
          :src="image"
          :placeholder="
            getPlaceholder(
              verticalTile
                ? 'masonryPlaceholderTwo'
                : 'categoryCardPlaceholder',
            )
          "
          :alt="title"
          :width="card?.scrollWidth"
          :height="card?.scrollHeight"
        />
        <GenericTag
          v-if="game?.exclusive"
          type="exclusive"
          position="top-left"
        />
        <GenericTag v-if="game?.isNew" type="new" position="bottom-left" />
        <GenericTag
          v-if="game?.isPromoted"
          type="promo"
          position="bottom-left"
        />
      </div>
      <div
        v-if="displayTitle"
        class="w-full overflow-hidden p-1 text-base-priority text-xs text-left"
      >
        <strong class="w-full line-clamp-1">
          {{ props.title || ' ' }}
        </strong>
        <div
          class="flex truncate w-full capitalize"
          :class="`${enableFreePlay ? 'justify-end' : 'justify-between'}`"
        >
          <div
            v-show="displayMinBet"
            :class="['minBetAmount', !game?.minBetAmount && 'opacity-0']"
            v-html="useFormatCurrency(minBetFormat)"
          />
          <div
            v-if="game?.likesCount"
            class="flex items-center gap-0.5 w-full justify-end"
          >
            <HandThumbUpIcon class="w-4" />
            {{ useFormatNumberCompact(game?.likesCount) }}
          </div>
        </div>
      </div>
    </GameUrlWrapper>
  </div>
</template>

<style scoped>
.game-card:hover > * > .launch-buttons {
  display: block;
}

.game-card.unavailable:hover,
.game-card.unavailable {
  cursor: not-allowed;
  pointer-events: none;
}

.game-card:not(:hover),
.game-card.unavailable {
  box-shadow: unset;
}

.header-top-item button {
  padding: 3.5px 5px !important;
  height: 20px !important;
}

@media screen and (max-width: 1024px) {
  .header-top-item button {
    font-size: 10px !important;
  }
}

::selection {
  background: transparent;
}
::-moz-selection {
  background: transparent;
}
</style>
